import { useState, useEffect, useCallback, useRef } from 'react'
import api, { API_BASE_URL } from '../lib/api'

// Resolve relative avatar URLs to full URLs using API base
function resolveAvatarUrl(agent: any): any {
  if (!agent?.avatarUrl) return agent
  if (agent.avatarUrl.startsWith('http') || agent.avatarUrl.startsWith('data:')) return agent
  return { ...agent, avatarUrl: `${API_BASE_URL}${agent.avatarUrl}` }
}

interface Agent {
  id: string
  email: string
  name: string
  role: string
  status: string
  department?: string
  avatarUrl?: string
}

interface Organization {
  id: string
  name: string
  slug: string
  role: string
  status: string
  subscriptionTier: string
}

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  agent: Agent | null
  token: string | null
  onboardingComplete: boolean
  sessionExpiryWarning: boolean
  sessionExpiresAt: Date | null
  requiresOrgSelection: boolean
  organizations: Organization[]
}

// Parse JWT to get expiry time
function parseJwt(token: string): { exp?: number } | null {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return null
  }
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    agent: null,
    token: null,
    onboardingComplete: false,
    sessionExpiryWarning: false,
    sessionExpiresAt: null,
    requiresOrgSelection: false,
    organizations: []
  })

  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null)
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Clean up timers
  const clearTimers = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current)
      warningTimerRef.current = null
    }
  }, [])

  // Set up session monitoring
  const setupSessionTimers = useCallback((token: string) => {
    clearTimers()

    const payload = parseJwt(token)
    if (!payload?.exp) return

    const expiresAt = new Date(payload.exp * 1000)
    const now = new Date()
    const timeUntilExpiry = expiresAt.getTime() - now.getTime()

    // Update expiry time in state
    setAuthState(prev => ({ ...prev, sessionExpiresAt: expiresAt }))

    // Set warning 5 minutes before expiry
    const warningTime = timeUntilExpiry - 5 * 60 * 1000
    if (warningTime > 0) {
      warningTimerRef.current = setTimeout(() => {
        setAuthState(prev => ({ ...prev, sessionExpiryWarning: true }))
      }, warningTime)
    }

    // Auto-refresh 10 minutes before expiry (if session is longer than 15 minutes)
    const refreshTime = timeUntilExpiry - 10 * 60 * 1000
    if (refreshTime > 0 && timeUntilExpiry > 15 * 60 * 1000) {
      refreshTimerRef.current = setTimeout(() => {
        refreshToken()
      }, refreshTime)
    }
  }, [clearTimers])

  // Initialize auth state from localStorage
  useEffect(() => {
    const token = localStorage.getItem('app_auth_token')
    const agentData = localStorage.getItem('app_agent_data')
    const storedOnboardingComplete = localStorage.getItem('app_onboarding_complete')

    if (token && agentData) {
      try {
        const agent = JSON.parse(agentData)
        const onboardingComplete = storedOnboardingComplete === 'true'

        // Parse token to get expiry
        const payload = parseJwt(token)
        const expiresAt = payload?.exp ? new Date(payload.exp * 1000) : null

        // Check if token is already expired
        if (expiresAt && expiresAt < new Date()) {
          console.log('[Auth] Stored token is expired, logging out')
          logout()
          return
        }

        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          agent: resolveAvatarUrl(agent),
          token,
          onboardingComplete,
          sessionExpiryWarning: false,
          sessionExpiresAt: expiresAt,
          requiresOrgSelection: false,
          organizations: []
        })

        // Set default auth header
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`

        // Set up session monitoring
        setupSessionTimers(token)

        // Verify token is still valid (will also update onboardingComplete if changed)
        verifyToken(token)
      } catch (error) {
        console.error('Error parsing stored auth data:', error)
        logout()
      }
    } else {
      setAuthState(prev => ({ ...prev, isLoading: false }))
    }

    // Cleanup on unmount
    return () => clearTimers()
  }, [])

  // Safari bfcache fix. When the user navigates back/forward (or taps a
  // Safari history link), Safari can restore the entire page from cache —
  // including stale React state. Without this listener, a user who logged
  // out and into a different account in another flow could land on a
  // history URL and see the OLD account's session in memory (e.g. admin
  // permissions resurrected after switching to an affiliate account).
  // localStorage is the source of truth: if it disagrees with React state
  // on the active token, force a full reload so useAuth re-initializes.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return // Not a bfcache restore — normal navigation, ignore
      const storageToken = localStorage.getItem('app_auth_token')
      // Read latest state via a fresh closure ref by checking authState below
      // (state is captured per-render, so re-read from setAuthState callback)
      setAuthState(prev => {
        if (storageToken !== prev.token) {
          // Storage and React state diverged while the page was bf-cached —
          // hard reload to start clean. Doing this from inside setAuthState
          // (instead of just calling reload()) so we only fire when the
          // tokens actually disagree, avoiding pointless reloads on every
          // back/forward when nothing changed.
          window.location.reload()
        }
        return prev
      })
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  const verifyToken = async (token: string) => {
    try {
      // Skip verification for demo tokens
      if (token.startsWith('demo-token-')) {
        return
      }

      const response = await api.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.data.success) {
        // /api/auth/me returns the org separately, but the rest of the app
        // (sidebar Plan badge, gating hooks) reads tier off the agent. Merge
        // it here so agent.subscriptionTier always reflects the live DB value,
        // not whatever was baked into the JWT at login time.
        const mergedAgent = {
          ...response.data.agent,
          subscriptionTier: response.data.organization?.subscriptionTier ?? response.data.agent.subscriptionTier,
        }

        // If the tier changed since the last known value, ping the feature
        // gate so it re-reads /api/billing/features immediately rather than
        // waiting for the 60s safety-net polling interval.
        try {
          const prevRaw = localStorage.getItem('app_agent_data')
          const prevTier = prevRaw ? JSON.parse(prevRaw)?.subscriptionTier : null
          if (prevTier !== mergedAgent.subscriptionTier) {
            window.dispatchEvent(new Event('app:tier-may-have-changed'))
          }
        } catch { /* ignore parse errors */ }

        setAuthState(prev => ({
          ...prev,
          agent: resolveAvatarUrl(mergedAgent),
          onboardingComplete: response.data.onboardingComplete ?? false
        }))

        // Update stored agent data with fresh data from server
        localStorage.setItem('app_agent_data', JSON.stringify(resolveAvatarUrl(mergedAgent)))
        localStorage.setItem('app_onboarding_complete', String(response.data.onboardingComplete ?? false))
        console.log('[Auth] Token verified successfully, role:', response.data.agent.role, 'onboardingComplete:', response.data.onboardingComplete)
      }
    } catch (error: any) {
      // Don't auto-logout on verification failure - could be network error or temporary API issue
      // Only logout if we get a clear 401 Unauthorized (invalid/expired token)
      if (error.response?.status === 401) {
        console.error('[Auth] Token verification failed - invalid or expired token:', error.response?.data?.message)
        // Only logout if the error message indicates invalid token
        if (error.response?.data?.message?.includes('Invalid token') ||
            error.response?.data?.message?.includes('token')) {
          console.warn('[Auth] Logging out due to invalid token')
          logout()
        }
      } else {
        // Network error or server error - don't logout, just log warning
        console.warn('[Auth] Token verification failed (network/server error), keeping session:', error.message)
      }
    }
  }

  const refreshToken = useCallback(async () => {
    try {
      console.log('[Auth] Refreshing token...')
      const response = await api.post('/api/auth/refresh')

      if (response.data.success) {
        const { token: newToken, expiresAt } = response.data

        // Update localStorage
        localStorage.setItem('app_auth_token', newToken)

        // Update auth header
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`

        // Update state
        setAuthState(prev => ({
          ...prev,
          token: newToken,
          sessionExpiryWarning: false,
          sessionExpiresAt: new Date(expiresAt)
        }))

        // Set up new session timers
        setupSessionTimers(newToken)

        console.log('[Auth] Token refreshed successfully')
        return { success: true }
      }
    } catch (error: any) {
      console.error('[Auth] Token refresh failed:', error)
      // If refresh fails with 401, the session is invalid - logout
      if (error.response?.status === 401) {
        logout()
      }
      return { success: false, message: error.response?.data?.message || 'Token refresh failed' }
    }
  }, [setupSessionTimers])

  const dismissSessionWarning = useCallback(() => {
    setAuthState(prev => ({ ...prev, sessionExpiryWarning: false }))
  }, [])

  const extendSession = useCallback(async () => {
    const result = await refreshToken()
    if (result?.success) {
      setAuthState(prev => ({ ...prev, sessionExpiryWarning: false }))
    }
    return result
  }, [refreshToken])

  const login = useCallback(async (email: string, password: string) => {
    try {
      // Don't flip global authState.isLoading here — App.tsx would unmount LoginPage
      // and the error message set in its local state after the API call would be lost.
      // LoginPage manages its own submit spinner.
      const response = await api.post('/api/auth/login', {
        email,
        password
      })

      if (response.data.success) {
        const { token, agent, onboardingComplete, requiresOrgSelection, organizations } = response.data

        // Parse token to get expiry
        const payload = parseJwt(token)
        const expiresAt = payload?.exp ? new Date(payload.exp * 1000) : null

        // Store in localStorage
        localStorage.setItem('app_auth_token', token)
        localStorage.setItem('app_agent_data', JSON.stringify(agent))
        localStorage.setItem('app_onboarding_complete', String(onboardingComplete ?? false))

        // Set default auth header
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`

        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          agent,
          token,
          onboardingComplete: onboardingComplete ?? false,
          sessionExpiryWarning: false,
          sessionExpiresAt: expiresAt,
          requiresOrgSelection: requiresOrgSelection ?? false,
          organizations: organizations ?? []
        })

        // Set up session monitoring
        setupSessionTimers(token)

        // Re-resolve the white-label brand for the freshly signed-in org
        // (the shared host can't infer it; BrandProvider will hit /api/brand/me).
        window.dispatchEvent(new Event('app:brand-may-have-changed'))

        return { success: true, requiresOrgSelection: requiresOrgSelection ?? false, organizations: organizations ?? [] }
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Login failed'
      return { success: false, message }
    }
  }, [setupSessionTimers])

  const selectOrganization = useCallback(async (organizationId: string) => {
    try {
      const response = await api.post('/api/auth/select-organization', { organizationId })

      if (response.data.success) {
        const { token, agent, organization, onboardingComplete } = response.data

        // Parse token to get expiry
        const payload = parseJwt(token)
        const expiresAt = payload?.exp ? new Date(payload.exp * 1000) : null

        // Update localStorage
        localStorage.setItem('app_auth_token', token)
        localStorage.setItem('app_agent_data', JSON.stringify(agent))
        localStorage.setItem('app_onboarding_complete', String(onboardingComplete ?? false))

        // Update auth header
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`

        setAuthState(prev => ({
          ...prev,
          token,
          agent,
          onboardingComplete: onboardingComplete ?? false,
          requiresOrgSelection: false,
          sessionExpiresAt: expiresAt
        }))

        setupSessionTimers(token)

        // Switching orgs may switch brands (e.g. into a franchise org).
        window.dispatchEvent(new Event('app:brand-may-have-changed'))

        return { success: true, organization }
      }
      return { success: false, message: 'Failed to switch organization' }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to switch organization'
      return { success: false, message }
    }
  }, [setupSessionTimers])

  const logout = useCallback(async () => {
    clearTimers()

    try {
      if (authState.token) {
        await api.post('/api/auth/logout')
      }
    } catch {
      // Logout API failure is non-critical — continue clearing local state
    } finally {
      localStorage.removeItem('app_auth_token')
      localStorage.removeItem('app_agent_data')
      localStorage.removeItem('app_onboarding_complete')
      // Drop any "viewing as <org>" overlay from the previous session — it
      // must not bleed into the next login (even a non-platform-admin one).
      localStorage.removeItem('app_impersonation')

      delete api.defaults.headers.common['Authorization']

      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        agent: null,
        token: null,
        onboardingComplete: false,
        sessionExpiryWarning: false,
        sessionExpiresAt: null,
        requiresOrgSelection: false,
        organizations: []
      })

      // Drop back to the hostname/default brand now the session is gone.
      window.dispatchEvent(new Event('app:brand-may-have-changed'))
    }
  }, [authState.token, clearTimers])

  const updateProfile = useCallback(async (profileData: Partial<Agent>) => {
    try {
      const response = await api.put('/api/auth/me', profileData)

      if (response.data.success) {
        const updatedAgent = response.data.agent

        setAuthState(prev => ({
          ...prev,
          agent: resolveAvatarUrl(updatedAgent)
        }))

        // Update stored agent data
        localStorage.setItem('app_agent_data', JSON.stringify(resolveAvatarUrl(updatedAgent)))

        return { success: true }
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Profile update failed'
      return { success: false, message }
    }
  }, [])

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    try {
      const response = await api.post('/api/auth/change-password', {
        currentPassword,
        newPassword
      })

      if (response.data.success) {
        return { success: true }
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Password change failed'
      return { success: false, message }
    }
  }, [])

  const refreshAgent = useCallback(async () => {
    const token = authState.token
    if (!token) return
    try {
      const response = await api.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.data.success) {
        const agent = resolveAvatarUrl(response.data.agent)
        setAuthState(prev => ({ ...prev, agent }))
        localStorage.setItem('app_agent_data', JSON.stringify(agent))
      }
    } catch {
      // Non-critical — agent data will refresh on next page load
    }
  }, [authState.token])

  return {
    ...authState,
    login,
    logout,
    selectOrganization,
    updateProfile,
    refreshAgent,
    changePassword,
    refreshToken,
    extendSession,
    dismissSessionWarning
  }
}
